import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import Overview from '../components/sections/Overview';
import GeographicView from '../components/sections/GeographicView';
import NetworkMap from '../components/sections/NetworkMap';
import NRWAnalysis from '../components/sections/NRWAnalysis';
import QualityMonitoring from '../components/sections/QualityMonitoring';
import AlertsPanel from '../components/sections/AlertsPanel';
import FlowHierarchy from '../components/sections/FlowHierarchy';
import Reports from '../components/sections/Reports';
import Settings from '../components/sections/Settings';
import SmartAnalytics from '../components/sections/SmartAnalytics';
import HistoricalData from '../components/sections/HistoricalData';
import { RealTimeProvider } from '../contexts/RealTimeContext';
import { LanguageProvider } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Dashboard() {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default to closed on mobile
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  // Global village data state - loaded from backend
  const [villageData, setVillageData] = useState({});

  // Responsive sidebar - open by default on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load villages from backend on mount
  const loadVillages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/villages`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVillageData(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading villages:', error);
      toast.error('Failed to load village data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVillages();
  }, [loadVillages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading SWATI Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <Sidebar 
        activeSection={activeSection}
        setActiveSection={(section) => {
          setActiveSection(section);
          setShowSettings(false);
        }}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onSettingsClick={() => {
            setShowSettings(true);
            setActiveSection('');
          }}
        />
        
        {/* Content Area - Responsive padding */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {showSettings ? (
            <Settings 
              villageData={villageData}
              setVillageData={setVillageData}
              onDataChange={loadVillages}
            />
          ) : (
            <>
              {activeSection === 'overview' && (
                <Overview villageData={villageData} />
              )}
              {activeSection === 'geographic' && (
                <GeographicView villageData={villageData} />
              )}
              {activeSection === 'network' && (
                <NetworkMap villageData={villageData} />
              )}
              {activeSection === 'nrw' && (
                <NRWAnalysis villageData={villageData} />
              )}
              {activeSection === 'quality' && (
                <QualityMonitoring villageData={villageData} />
              )}
              {activeSection === 'alerts' && (
                <AlertsPanel villageData={villageData} />
              )}
              {activeSection === 'flow' && (
                <FlowHierarchy villageData={villageData} />
              )}
              {activeSection === 'reports' && (
                <Reports />
              )}
              {activeSection === 'smart-analytics' && (
                <SmartAnalytics />
              )}
              {activeSection === 'historical' && (
                <HistoricalData />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

// Wrap with providers
export default function DashboardWithProviders() {
  return (
    <LanguageProvider>
      <RealTimeProvider>
        <Dashboard />
      </RealTimeProvider>
    </LanguageProvider>
  );
}